import update from "immutability-helper";
import Date from "libs/date";
import chunk from "lodash-es/chunk";
import isEqual from "lodash-es/isEqual";
import mapValues from "lodash-es/mapValues";
import sumBy from "lodash-es/sumBy";
import { getStats, type TracingStats } from "viewer/model/accessors/annotation_accessor";
import type { Action } from "viewer/model/actions/actions";
import { getActionLog } from "viewer/model/helpers/action_logger_middleware";
import { updateKey, updateKey2 } from "viewer/model/helpers/deep_update";
import { MAXIMUM_ACTION_COUNT_PER_BATCH } from "viewer/model/sagas/saving/save_saga_constants";
import type { SaveState, WebknossosState } from "viewer/store";

// These update actions are not idempotent. Having them
// twice in the save queue causes a corruption of the current annotation.
// Therefore, we check this.
const NOT_IDEMPOTENT_ACTIONS = [
  "createEdge",
  "deleteEdge",
  "createTree",
  "deleteTree",
  "createNode",
  "deleteNode",
];

export function getTotalSaveQueueLength(queueObj: SaveState["queue"]) {
  return queueObj.length;
}

function SaveReducer(state: WebknossosState, action: Action): WebknossosState {
  switch (action.type) {
    case "PUSH_SAVE_QUEUE_TRANSACTION": {
      // Use `dispatchedAction` to better distinguish this variable from
      // update actions.
      const dispatchedAction = action;
      if (dispatchedAction.items.length === 0) {
        console.warn("PUSH_SAVE_QUEUE_TRANSACTION was dispatched with empty items.");
        return state;
      }
      const { items, transactionId } = dispatchedAction;
      const stats: TracingStats = getStats(state.annotation);
      const { activeUser } = state;
      if (activeUser == null) {
        throw new Error("Tried to save something even though user is not logged in.");
      }

      const updateActionChunks = chunk(items, MAXIMUM_ACTION_COUNT_PER_BATCH);

      const transactionGroupCount = updateActionChunks.length;
      const actionLogInfo = JSON.stringify(getActionLog().slice(-10));
      const oldQueue = state.save.queue;
      const newQueue = oldQueue.concat(
        updateActionChunks.map((actions, transactionGroupIndex) => ({
          // Placeholder, the version number will be updated before sending to the server
          version: -1,
          transactionId,
          transactionGroupCount,
          transactionGroupIndex,
          timestamp: Date.now(),
          authorId: activeUser.id,
          actions,
          stats,
          // Redux Action Log context for debugging purposes.
          info: actionLogInfo,
        })),
      );

      // The following code checks that the update actions are not identical to the previously
      // added ones. We have received a bug report that showed corrupted data that should be
      // caught by the following check. If the bug appears again, we can investigate with more
      // details thanks to airbrake.
      if (
        oldQueue.length > 0 &&
        newQueue.length > 0 &&
        newQueue.at(-1)?.actions.some((action) => NOT_IDEMPOTENT_ACTIONS.includes(action.name)) &&
        isEqual(oldQueue.at(-1)?.actions, newQueue.at(-1)?.actions)
      ) {
        console.warn(
          "Redundant saving was detected.",
          oldQueue.at(-1)?.actions,
          newQueue.at(-1)?.actions,
        );
        throw new Error(
          "An internal error has occurred. To prevent data corruption, no saving was performed. Please reload the page and try the last action again.",
        );
      }

      return update(state, {
        save: {
          queue: {
            $set: newQueue,
          },
          progressInfo: {
            totalActionCount: {
              $apply: (count) => count + items.length,
            },
          },
        },
      });
    }

    case "SHIFT_SAVE_QUEUE": {
      const { count } = action;

      if (count > 0) {
        const queue = state.save.queue;

        const processedQueueActionCount = sumBy(
          queue.slice(0, count),
          (batch) => batch.actions.length,
        );

        const remainingQueue = queue.slice(count);
        const remainingQueueLength = getTotalSaveQueueLength(remainingQueue);
        const resetCounter = remainingQueueLength === 0;
        return update(state, {
          save: {
            queue: {
              $set: remainingQueue,
            },
            progressInfo: {
              // Reset progress counters if the queue is empty. Otherwise,
              // increase processedActionCount and leave totalActionCount as is
              processedActionCount: {
                $apply: (oldCount) => (resetCounter ? 0 : oldCount + processedQueueActionCount),
              },
              totalActionCount: {
                $apply: (oldCount) => (resetCounter ? 0 : oldCount),
              },
            },
          },
        });
      }

      return state;
    }

    case "DISCARD_SAVE_QUEUE": {
      return update(state, {
        save: {
          queue: {
            $set: [],
          },
          progressInfo: {
            processedActionCount: {
              $set: 0,
            },
            totalActionCount: {
              $set: 0,
            },
          },
        },
      });
    }

    case "REPLACE_SAVE_QUEUE": {
      // Only used during rebasing to update save queue entries in case their data was outdated and needed syncing with the newest backend version.
      return update(state, {
        save: {
          queue: {
            $set: action.newSaveQueue,
          },
        },
      });
    }

    case "SET_LAST_SAVE_TIMESTAMP": {
      return updateKey(state, "save", { lastSaveTimestamp: action.timestamp });
    }

    case "SET_VERSION_NUMBER": {
      return updateKey(state, "annotation", {
        version: action.version,
      });
    }

    case "DISABLE_SAVING": {
      if (state.task != null) {
        // Don't disable saving in a task, even when this action was dispatched somehow.
        return state;
      }

      return update(state, {
        annotation: { restrictions: { allowSave: { $set: false } } },
        save: { isSavingDisabled: { $set: true } },
      });
    }

    case "SET_IS_MUTEX_ACQUIRED": {
      const { isMutexAcquired } = action;
      return updateKey2(state, "save", "mutexState", {
        hasAnnotationMutex: isMutexAcquired,
      });
    }

    case "SET_USER_HOLDING_MUTEX": {
      const { blockedByUser, blockedBySessionId } = action;
      return updateKey2(state, "save", "mutexState", {
        blockedByUser,
        blockedBySessionId: blockedBySessionId ?? null,
      });
    }

    case "REWIND_FOR_REBASE": {
      const rebaseInfo = state.save.rebaseRelevantServerAnnotationState;
      // Only the mapping data is part of the rebase baseline. All other ActiveMappingInfo
      // fields are local view state that no update action carries, so we keep their live
      // values and only replace the mapping data for the layers that have a snapshot entry (see #9559).
      const restoredMappingByLayer = mapValues(
        state.temporaryConfiguration.activeMappingByLayer,
        (liveMappingInfo, layerName) =>
          layerName in rebaseInfo.mappingDataByLayer
            ? { ...liveMappingInfo, mapping: rebaseInfo.mappingDataByLayer[layerName] }
            : liveMappingInfo,
      );
      return update(state, {
        annotation: {
          version: {
            $set: rebaseInfo.annotationVersion,
          },
          description: {
            $set: rebaseInfo.annotationDescription,
          },
          skeleton: {
            $set: rebaseInfo.skeleton,
          },
          volumes: { $set: rebaseInfo.volumes },
        },
        temporaryConfiguration: {
          activeMappingByLayer: {
            $set: restoredMappingByLayer,
          },
        },
        save: {
          rebaseRelevantServerAnnotationState: {
            isRebasingOrForwarding: { $set: true },
          },
        },
      });
    }

    case "START_FORWARDING_UPDATE_ACTIONS": {
      return update(state, {
        save: {
          rebaseRelevantServerAnnotationState: {
            isRebasingOrForwarding: { $set: true },
          },
        },
      });
    }

    case "FINISHED_REBASING":
    case "FINISH_FORWARDING_UPDATE_ACTIONS": {
      return update(state, {
        save: {
          rebaseRelevantServerAnnotationState: {
            isRebasingOrForwarding: { $set: false },
          },
        },
      });
    }

    case "SNAPSHOT_MAPPING_DATA_FOR_NEXT_REBASE_ACTION": {
      const mappingInfoOfLayer =
        state.temporaryConfiguration.activeMappingByLayer[action.volumeLayerIdToUpdate];
      return update(state, {
        save: {
          rebaseRelevantServerAnnotationState: {
            mappingDataByLayer: {
              [action.volumeLayerIdToUpdate]: {
                $set: mappingInfoOfLayer?.mapping,
              },
            },
          },
        },
      });
    }

    case "SNAPSHOT_ANNOTATION_STATE_FOR_NEXT_REBASE":
    case "FINISHED_APPLYING_MISSING_UPDATES": {
      return update(state, {
        save: {
          rebaseRelevantServerAnnotationState: {
            annotationDescription: {
              $set: state.annotation.description,
            },
            annotationVersion: {
              $set: state.annotation.version,
            },
            mappingDataByLayer: {
              $set: mapValues(
                state.temporaryConfiguration.activeMappingByLayer,
                (mappingInfo) => mappingInfo.mapping,
              ),
            },
            skeleton: {
              $set: state.annotation.skeleton,
            },
            volumes: {
              $set: state.annotation.volumes,
            },
          },
        },
      });
    }

    case "SET_PENDING_PROOFREADING_OPERATION_INFO": {
      return update(state, {
        save: {
          proofreadingPostProcessingInfo: { $set: action.proofreadingPostProcessingInfo },
        },
      });
    }

    default:
      return state;
  }
}

export default SaveReducer;
